import React, { useEffect, useState } from "react";
import "./footer.css";
import {
    FaInstagram,
    FaFacebookF,
    FaTwitter,
    FaYoutube,
    FaLinkedinIn,
} from "react-icons/fa";
import { FaLocationDot, FaMobileScreen } from "react-icons/fa6";
import { IoIosMail } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../../Services/axiosConfig";

function Footer() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [siteData, setSiteData] = useState(null);
    const [siteBranding, setSiteBranding] = useState({
        companyName: localStorage.getItem("companyName") || "",
        logoUrl: localStorage.getItem("siteLogo") || "",
    });

    const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    };

    const categoryChunks = categories.length > 0 ? chunkArray(categories, 6) : [[]];

    const getCategories = async () => {
        try {
            const response = await axiosConfig.get(
                "/catlog/categories/?page_size=30&is_suspended=false"
            );
            setCategories(response.data.results || []);
        } catch (error) {
            console.log(error);
        }
    };

    const getSiteData = async () => {
        try {
            let res;
            try {
                res = await axiosConfig.get("/sitedata/site-config/");
            } catch (err) {
                console.warn("site-config failed in footer, trying /sitedata/site/:", err);
                res = await axiosConfig.get("/sitedata/site/");
            }

            const data = res?.data;
            const site = data?.site || (data?.results?.[0] ? data.results[0] : null);

            const companyName =
                data?.tenant?.company_name ||
                site?.title ||
                "";
            const logoUrl = data?.theme?.logo_url || site?.logo?.image || "";

            if (logoUrl) {
                localStorage.setItem("siteLogo", logoUrl);
            }
            if (companyName) {
                localStorage.setItem("companyName", companyName);
            }

            setSiteBranding({
                companyName,
                logoUrl: logoUrl || localStorage.getItem("siteLogo") || "",
            });

            if (site) {
                setSiteData(site);
            }
        } catch (error) {
            console.warn("Failed to fetch site config in footer:", error);
        }
    };

    useEffect(() => {
        getCategories();
        getSiteData();

        const handleStorageChange = () => {
            setSiteBranding({
                companyName: localStorage.getItem("companyName") || "",
                logoUrl: localStorage.getItem("siteLogo") || "",
            });
        };

        window.addEventListener("siteBrandingUpdated", handleStorageChange);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("siteBrandingUpdated", handleStorageChange);
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    const locationAddress =
        siteData?.contact?.address ||
        siteData?.address ||
        "Texas, United States";

    const phoneNumber =
        siteData?.contact?.mobile ||
        siteData?.contact?.phone ||
        siteData?.phone ||
        "408-216-7968";

    const emailAddress =
        siteData?.contact?.email ||
        siteData?.email ||
        "4sridar@gmail.com";

    const facebookUrl = siteData?.social?.facebook || siteData?.facebook;
    const instagramUrl = siteData?.social?.instagram || siteData?.instagram;
    const linkedinUrl =
        siteData?.social?.linkedin ||
        siteData?.social?.linkedn ||
        siteData?.linkedin ||
        siteData?.linkedn;
    const twitterUrl = siteData?.social?.twitter || siteData?.twitter;
    const youtubeUrl = siteData?.social?.youtube || siteData?.youtube;
    const hasSocials = Boolean(
        facebookUrl ||
        instagramUrl ||
        linkedinUrl ||
        twitterUrl ||
        youtubeUrl
    );

    const brandTitle =
        siteData?.title ||
        siteBranding.companyName ||
        "";

    return (
        <footer className="footer-container">
            <div className="footer-top">
                {/* BRAND & SOCIALS */}
                <div className="footer-brand">
                    {siteBranding.logoUrl ? (
                        <img
                            src={siteBranding.logoUrl}
                            alt={brandTitle}
                            className="footer-logo"
                            onClick={() => {
                                navigate("/");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        />
                    ) : (
                        <h2
                            className="footer-logo-title"
                            onClick={() => {
                                navigate("/");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        >
                            {brandTitle}
                        </h2>
                    )}

                    {siteData?.description && (
                        <p className="footer-description">
                            {siteData.description}
                        </p>
                    )}

                    {hasSocials && (
                        <div className="footer-socials">
                            {instagramUrl && (
                                <a
                                    href={instagramUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Instagram"
                                >
                                    <FaInstagram />
                                </a>
                            )}
                            {facebookUrl && (
                                <a
                                    href={facebookUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Facebook"
                                >
                                    <FaFacebookF />
                                </a>
                            )}
                            {linkedinUrl && (
                                <a
                                    href={linkedinUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="LinkedIn"
                                >
                                    <FaLinkedinIn />
                                </a>
                            )}
                            {twitterUrl && (
                                <a
                                    href={twitterUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Twitter"
                                >
                                    <FaTwitter />
                                </a>
                            )}
                            {youtubeUrl && (
                                <a
                                    href={youtubeUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="YouTube"
                                >
                                    <FaYoutube />
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {/* CATEGORIES */}
                {categoryChunks.map((chunk, chunkIndex) => (
                    <div className="footer-links" key={chunkIndex}>
                        <h4
                            style={{
                                fontSize: "16px",
                                marginBottom: "12px",
                                color: "#333",
                                marginTop: "0",
                                visibility: chunkIndex === 0 ? "visible" : "hidden",
                            }}
                        >
                            CATEGORIES
                        </h4>
                        {chunk.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    navigate(`/${item.slug}`);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                            >
                                {item.name}
                            </div>
                        ))}
                    </div>
                ))}

                {/* GET IN TOUCH (Referred from site-exit) */}
                <div className="footer-contact-section">
                    <h3>Get In Touch</h3>

                    <div className="footer-contact-card">
                        <div className="contact-icon">
                            <FaLocationDot />
                        </div>
                        <div>
                            <span>Location</span>
                            <p>{locationAddress}</p>
                        </div>
                    </div>

                    <div className="footer-contact-card">
                        <div className="contact-icon">
                            <FaMobileScreen />
                        </div>
                        <div>
                            <span>Phone Number</span>
                            <p>
                                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                            </p>
                        </div>
                    </div>

                    <div className="footer-contact-card">
                        <div className="contact-icon">
                            <IoIosMail />
                        </div>
                        <div>
                            <span>Email Address</span>
                            <p className="footer-email">
                                <a href={`mailto:${emailAddress}`}>{emailAddress}</a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* FOOTER BOTTOM */}
            <div className="footer-bottom">
                <p>We deliver across:</p>
                <div className="footer-cities">
                    Austin & Dallas
               </div>

                <div className="footer-bottom-container">
                    <p className="footer-copyright">
                        © {new Date().getFullYear()} {brandTitle}. All Rights Reserved.
                    </p>

                    <div className="footer-legal-links">
                        <span
                            onClick={() => {
                                navigate("/privacy");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        >
                            Privacy Policy
                        </span>
                        <span className="legal-divider">|</span>
                        <span
                            onClick={() => {
                                navigate("/terms-of-service");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        >
                            Terms of Service
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default Footer;
